import React, { useEffect, useMemo, useRef, useState } from 'react';
import HeaderBar from '../CrystalContainer/HeaderBar';
import RMTable, { RowExtraInfo } from 'components/customComponents/Controls/RMTable';
import {
  ImagingSchedulesDTO,
  ImagingSchedulesDeletedDataDTO,
} from 'business/models/Models';
import './Imaging.scss';
import { useDispatch } from 'react-redux';
import {
  setImagingSchedulesIsDirty,
  setSelectedImagingScheduleID,
} from 'redux/reducers/imagingSchedulesReducer';
import { RMModalProps } from 'components/modal/RMModal';
import {
  ImageScheduleListCols,
  getRandomNegativeNumber,
  getScheduleDefaultMessage,
  getScheduleDeleteMessage,
} from './utils';
import { useImagingSchedulesIsDirty } from 'redux/hooks/imagingSetup';
import ImagingScheduleService from 'business/services/ImagingScheduleService';

interface ImagingScheduleListProps {
  imagingSchedulesDto: ImagingSchedulesDTO[];
  setImagingScheduleDto: React.Dispatch<React.SetStateAction<ImagingSchedulesDTO[]>>;
  setDeletedData: React.Dispatch<React.SetStateAction<ImagingSchedulesDeletedDataDTO>>;
  scheduleErrors?: RowExtraInfo;
  setModal?: React.Dispatch<React.SetStateAction<RMModalProps>>;
}

const imagingScheduleService = new ImagingScheduleService();

function ImagingScheduleList(props: ImagingScheduleListProps) {
  const {
    imagingSchedulesDto,
    setImagingScheduleDto,
    setDeletedData,
    scheduleErrors,
    setModal,
  } = props;
  const [selectedIds, setSelectedIds] = useState<number[]>([0]);
  const [editId, setEditId] = useState<number>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch();
  const isDirty = useImagingSchedulesIsDirty();

  const hideModal = () => {
    if (setModal) {
      setModal((prev) => ({
        ...prev,
        showModal: false,
      }));
    }
  };

  const showDefaultScheduleMessage = () => {
    const message = getScheduleDefaultMessage();
    if (setModal) {
      setModal((prev) => ({
        ...prev,
        showModal: true,
        body: message,
        heading: 'Enable',
        okButtonText: 'OK',
        isOkOnly: true,
        onHide: hideModal,
      }));
    }
  };

  const handleEnable = async (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number,
    index: number,
  ) => {
    const isDefault = await imagingScheduleService.getIsImagingScheduleDefault(id);
    if (isDefault) {
      showDefaultScheduleMessage();
    } else {
      onCellChange(e, index);
    }
  };

  const onCellChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, type, value } = e.target;
    setImagingScheduleDto((prev) => {
      const _idx = tableData.findIndex((x) => x.index === index);
      const updatedPrev = [...prev];
      const updatedItem = { ...updatedPrev[_idx] };
      if (type === 'checkbox' && name === 'enable') {
        updatedItem[name] = !updatedItem[name];
      } else {
        updatedItem[name] = value;
      }
      updatedPrev[_idx] = new ImagingSchedulesDTO(updatedItem);
      return updatedPrev;
    });
    dispatch(setImagingSchedulesIsDirty(true));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setEditId(undefined);
    }
  };

  const getEnableCheckbox = (id: number, enable: boolean, idx: number) => {
    return (
      <input
        id="enablecheckbox"
        name="enable"
        type="checkbox"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleEnable(e, id, idx)}
        checked={enable}
        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
      />
    );
  };

  const tableData = useMemo(() => {
    if (imagingSchedulesDto) {
      return imagingSchedulesDto?.map((sch: ImagingSchedulesDTO, _idx) => ({
        index: _idx,
        id: sch.id,
        name:
          editId !== sch.id ? (
            <div
              id="imagingschedulelist-name"
              className="!border-0 bg-transparent p-2 w-full min-h-[2rem]"
            >
              {sch.name}
            </div>
          ) : (
            <input
              id="imagingschedulelist-input"
              type="text"
              key={sch.id}
              name="name"
              value={sch.name}
              className="bg-white p-2 w-full"
              style={{ border: '1px solid black', borderRadius: '0.25rem' }}
              onChange={(e) => {
                onCellChange(e, _idx);
              }}
              ref={inputRef}
              onBlur={() => setEditId(undefined)}
              onKeyDown={handleKeyPress}
            />
          ),
        enable: getEnableCheckbox(sch.id, sch.enable, _idx),
      }));
    }
    return [];
  }, [imagingSchedulesDto, editId]);

  const handleOnRowClick = (row) => {
    dispatch(setSelectedImagingScheduleID(imagingSchedulesDto[row.index]?.id));
    setSelectedIds([row.index]);
  };

  const onAdd = () => {
    const rndNum = getRandomNegativeNumber();
    const newData = [...imagingSchedulesDto];
    const newDataItem = new ImagingSchedulesDTO({
      id: rndNum,
      name: '',
      enable: true,
    });
    newData.push(newDataItem);
    setImagingScheduleDto(newData);
    setSelectedIds([newData.length - 1]);
    dispatch(setImagingSchedulesIsDirty(true));
    setEditId(rndNum);
    setTimeout(() => {
      if (inputRef?.current) {
        inputRef.current.focus();
      }
    }, 200);
  };

  const showDeleteMessage = () => {
    const message = getScheduleDeleteMessage(selectedIds.length);
    if (setModal) {
      setModal((prev) => ({
        ...prev,
        showModal: true,
        body: message,
        heading: `Delete`,
        okButtonText: 'OK',
        isOkOnly: true,
        onHide: hideModal,
      }));
    }
  };

  const onEdit = () => {
    setEditId(imagingSchedulesDto[selectedIds[0]]?.id);
    setTimeout(() => {
      if (inputRef?.current) {
        inputRef.current.focus();
      }
    }, 200);
  };

  const onDelete = async () => {
    const ids = selectedIds.map((i) => imagingSchedulesDto[i]?.id);
    const existingIds = ids.filter((id) => id > 0);
    const isInUse = await imagingScheduleService.getIsImagingScheduleInUse(
      existingIds[0],
    );
    if (isInUse) {
      showDeleteMessage();
      return;
    }
    const newData = imagingSchedulesDto.filter((x) => !ids.includes(x.id));
    setImagingScheduleDto(newData);
    setDeletedData(
      (prevData) =>
        new ImagingSchedulesDeletedDataDTO({
          ...prevData,
          imagingScheduleIds: Array.from(
            new Set([...(prevData?.imagingScheduleIds || []), ...existingIds]),
          ),
        }),
    );
    dispatch(setImagingSchedulesIsDirty(true));
    setSelectedIds([]);
    dispatch(setSelectedImagingScheduleID(0));
  };

  useEffect(() => {
    if (imagingSchedulesDto) {
      setImagingScheduleDto(imagingSchedulesDto);
      dispatch(setSelectedImagingScheduleID(imagingSchedulesDto[selectedIds[0]]?.id));
    }
  }, [imagingSchedulesDto]);

  useEffect(() => {
    if (!isDirty) {
      setEditId(undefined);
    }
  }, [isDirty]);
  return (
    <div id="imagingschedulelist-wrapper" className="flex flex-col h-full">
      <HeaderBar
        title="Schedule Name"
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={() => onDelete()}
        enableEditDelete={selectedIds?.length > 0}
      />
      <RMTable
        columns={[...ImageScheduleListCols]}
        data={tableData}
        tableClass="w-full h-full table-auto"
        selectedIds={selectedIds}
        onRowClicked={handleOnRowClick}
        rowExtraInfo={scheduleErrors}
      />
    </div>
  );
}

export default ImagingScheduleList;
